/**
 * The navigation tree, as data (plan F2).
 *
 * Phases 05–07 add a screen by adding an entry here and a route element. They
 * never edit `AppShell` or `Sidebar`, which is what keeps "add a screen" from
 * meaning "touch the shell every tenant sees".
 *
 * **`permission` and `feature` are imported types, never string literals.**
 * `PermissionKey` and `FeatureKey` come from `@rd/shared`, so a key renamed in
 * the contract is a **compile error here**, not a nav item that silently stops
 * matching and quietly disappears for everyone. Criterion 13 verifies this by
 * renaming a key and requiring the web build to break.
 *
 * The two are separate fields because they are separate questions (§4, §5):
 * `permission` is "may this person", `feature` is "has this account bought it".
 * A viewer without `sims.write` and a tenant without the messaging entitlement
 * are different situations with different remedies.
 */

import type { FeatureKey, PermissionKey } from '@rd/shared';
import {
  Cpu,
  Download,
  LayoutDashboard,
  ListChecks,
  ScrollText,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Which part of the sidebar an item belongs to.
 *
 * The client asked for "just SIM activations, the SIM activated status and swap
 * SIM functionality", and chose **keep-and-de-emphasise** for the rest. So
 * nothing is deleted and every URL still works — the demoted screens simply
 * stop competing for attention with the three flows the panel is actually for.
 *
 * **Jobs is primary**, not secondary: activation and swap are job-backed, so
 * tracking them is part of the flow rather than an administrative extra.
 */
export type NavSection = 'primary' | 'secondary';

export interface NavItem {
  label: string;
  to: string;
  section: NavSection;
  /**
   * The item's icon, as a component.
   *
   * Phase 04 shipped single unicode glyphs (`▤`, `⚿`) because no icon set was
   * bundled. They rendered in whatever the system font had, so they varied by
   * platform, sat off the text baseline, and could not take a stroke weight.
   * Lucide is bundled locally and tree-shaken — each icon below is a named
   * import, so only the nine here reach the bundle, and **nothing is fetched
   * from a CDN at runtime**.
   */
  icon: LucideIcon;
  permission: PermissionKey;
  feature?: FeatureKey;
  children?: NavItem[];
}

export const NAV: readonly NavItem[] = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    section: 'primary',
    icon: LayoutDashboard,
    permission: 'account.read',
    feature: 'account.overview',
  },
  {
    label: 'SIMs',
    to: '/sims',
    section: 'primary',
    icon: Cpu,
    permission: 'sims.read',
    feature: 'inventory.baskets',
  },
  {
    label: 'Jobs',
    to: '/jobs',
    // Primary: activation and swap are job-backed, so this is integral.
    section: 'primary',
    icon: ListChecks,
    permission: 'jobs.read',
    feature: 'jobs.tracking',
  },
  {
    label: 'Exports',
    section: 'secondary',
    to: '/exports',
    icon: Download,
    permission: 'sims.export',
    feature: 'inventory.export',
  },
  /*
    ACCOUNT and BRANDING are deliberately absent from this list, and both
    routes still exist and answer on a direct URL.

    Account is gated server-side on exactly what the Dashboard is
    (`account.read` + `account.overview`), so it cannot be hidden for one
    tenant without hiding the Dashboard too — entitlement cannot separate them,
    and a tenant that wants only Dashboard/SIMs/Jobs must therefore lose the
    nav entry rather than the feature. Branding is the white-label operator's
    tool rather than the customer's, and is gated on permission alone, so it
    likewise has no per-tenant switch. Everything else below IS switchable per
    tenant through its feature, which is why it stays in the list.

    Users, Roles and Audit carry the feature the API actually requires — every
    route in `users.routes.ts`, `roles.routes.ts` and `audit.routes.ts` runs
    `requireFeature` — so the nav stops advertising screens that can only 403
    for an account without the entitlement. Branding is deliberately
    feature-less: the server gates it on permission only, and inventing a
    stricter gate here would hide a screen the API would happily serve.
  */
  {
    label: 'Users',
    section: 'secondary',
    to: '/users',
    icon: Users,
    permission: 'users.read',
    feature: 'platform.user_management',
  },
  {
    label: 'Roles',
    section: 'secondary',
    to: '/roles',
    icon: ShieldCheck,
    permission: 'roles.read',
    feature: 'platform.user_management',
  },
  {
    label: 'Audit log',
    section: 'secondary',
    to: '/audit',
    icon: ScrollText,
    permission: 'audit.read',
    feature: 'platform.audit_log',
  },
];

